/**
 * Сессия приложения: кто вошёл, в какой семье работаем, какая у него роль.
 *
 * Стор намеренно тонкий: источник правды — сервер и React Query, здесь только
 * то, что нужно синхронно и повсеместно (роль, id семьи, таймзона).
 */

import { create } from 'zustand'

import type { Family, Me, Membership, Role } from '@/shared/api/types'

interface SessionState {
  me?: Me
  activeFamilyId?: number
  setMe: (me: Me) => void
  setActiveFamily: (familyId: number) => void
  clear: () => void
}

export const useSession = create<SessionState>((set) => ({
  me: undefined,
  activeFamilyId: undefined,
  setMe: (me) =>
    set((state) => ({
      me,
      // не сбрасываем выбранную семью, если она всё ещё доступна
      activeFamilyId:
        state.activeFamilyId && me.memberships.some((m) => m.family_id === state.activeFamilyId)
          ? state.activeFamilyId
          : (me.active_family_id ?? undefined),
    })),
  setActiveFamily: (familyId) => set({ activeFamilyId: familyId }),
  clear: () => set({ me: undefined, activeFamilyId: undefined }),
}))

export function activeMembership(state: SessionState): Membership | undefined {
  if (!state.me) return undefined
  return (
    state.me.memberships.find((m) => m.family_id === state.activeFamilyId) ??
    state.me.memberships[0]
  )
}

export function activeFamily(state: SessionState): Family | undefined {
  if (!state.me) return undefined
  return state.me.families.find((f) => f.id === state.activeFamilyId) ?? state.me.families[0]
}

export function useRole(): Role | undefined {
  return useSession((state) => activeMembership(state)?.role)
}

export function useFamily(): Family | undefined {
  return useSession(activeFamily)
}

/** Таймзона семьи — все даты в интерфейсе считаются в ней, не в устройстве. */
export function useTimeZone(): string {
  return useSession((state) => activeFamily(state)?.timezone ?? 'Europe/Moscow')
}

export function useCurrency(): string {
  return useSession((state) => activeFamily(state)?.currency ?? 'RUB')
}
